import { camelize } from 'helpers/syntaxHelper';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import {
    useMutation,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { dashDateStringToMonth } from 'routes/HumanResourcesModule/business/helpers';
import {
    HrSimulation,
    HrSimulationCreatePayload,
    HrSimulationDuplicatePayload,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { dispatch } from 'store/store';
import { SIMULATION_ENDPOINT_PREFIX } from '../constants';
import { useTranslation } from 'react-i18next';

export const useGetAllHrSimulations = (): UseQueryResult<HrSimulation[]> => {
    const endpoint = 'human_resources/all_hr_simulation';

    return useQuery({
        queryKey: ['all_simulations'],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};
export type ObsoleteServicesBySimulation = {
    [key: string]: (null | string)[];
};
export const useGetAllObsoleteOrgChartHrSimulation =
    (): UseQueryResult<ObsoleteServicesBySimulation> => {
        const endpoint = `human_resources/all_obsolete_org_chart_by_hr_simulation`;

        return useQuery({
            queryKey: ['all_obsolete_org_chart_by_hr_simulation'],
            queryFn: async () => {
                const getResponse = await RQAPICall({
                    endpoint,
                    method: 'GET',
                });
                return getResponse.content.data;
            },
            meta: {
                useGlobalOnError: true,
            },
        });
    };
export const useGetHrSimulation = (
    id: number,
): UseQueryResult<HrSimulation> => {
    const endpoint = `${SIMULATION_ENDPOINT_PREFIX}/${id}`;

    return useQuery({
        queryKey: ['hr_simulation', id],
        queryFn: async () => {
            const getResponse = await RQAPICall({
                endpoint,
                method: 'GET',
            });
            return getResponse.content.data;
        },
        meta: {
            useGlobalOnError: true,
        },
    });
};

const useHrSimulationMutation = (endpoint: string, eventName: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    return useMutation({
        mutationFn: (
            payload: HrSimulationCreatePayload | HrSimulationDuplicatePayload,
        ) =>
            RQAPICallWithData<HrSimulation>({
                endpoint,
                method: 'POST',
                body: payload,
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['all_simulations'],
                (oldData: HrSimulation[]) =>
                    oldData
                        ? [...oldData, response.content.data]
                        : [response.content.data],
            );
            trackEventPRH(eventName, {
                type: t(
                    `humanResources.simulation.${camelize(
                        response.content.data.type,
                    )}`,
                ),
                recurrentInitialisationMonth: dashDateStringToMonth(
                    response.content.data.recurrentInitialisationStartDate,
                ),
                servicesNumber: response.content.data.orgChartHrs.length,
                etablissementFilter:
                    response.content.data.isEtablissementsFilterActive,
                budgetFilter: response.content.data.isPayBudgetFilterActive,
                simulationId: response.content.data.id,
            });
        },
        meta: { useGlobalOnError: true },
    });
};

export const useCreateHrSimulation = () =>
    useHrSimulationMutation(
        `${SIMULATION_ENDPOINT_PREFIX}`,
        TRACKABLE_EVENTS.human_resources.simulation.create,
    );

export const useDuplicateHrSimulation = (id: number) =>
    useHrSimulationMutation(
        `${SIMULATION_ENDPOINT_PREFIX}/duplicate/${id}`,
        TRACKABLE_EVENTS.human_resources.simulation.duplicate,
    );

export const useDeleteHrSimulation = (id: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () =>
            RQAPICall({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${id}`,
                method: 'DELETE',
            }),

        onSuccess: (response) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                ['all_simulations'],
                (oldData: HrSimulation[]) =>
                    oldData.filter((simulation) => simulation.id !== id),
            );
        },
        meta: { useGlobalOnError: true },
    });
};
